import add from "date-fns/add";
import differenceInMinutes from "date-fns/differenceInMinutes";
import format from "date-fns/format";
import formatDistance from "date-fns/formatDistance";
import formatISO from "date-fns/formatISO";
import locale from "date-fns/locale/en-US";
import parseISO from "date-fns/parseISO";
import previousSunday from "date-fns/previousSunday";
import formatTz from "date-fns-tz/format";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import isClient from "./isClient";
import logger from "./logger";

const formatDistanceLocale: Record<string, string> = {
  lessThanXSeconds: "{{count}}s",
  xSeconds: "{{count}}s",
  halfAMinute: "30s",
  lessThanXMinutes: "{{count}}m",
  xMinutes: "{{count}}m",
  aboutXHours: "{{count}}h",
  xHours: "{{count}}h",
  xDays: "{{count}}d",
  aboutXWeeks: "{{count}}w",
  xWeeks: "{{count}}w",
  aboutXMonths: "{{count}}mo",
  xMonths: "{{count}}mo",
  aboutXYears: "{{count}}y",
  xYears: "{{count}}y",
  overXYears: "{{count}}y",
  almostXYears: "{{count}}y",
};

const formatSingleDistanceLocale: Record<string, string> = {
  lessThanXMinutes: "{{count}}m",
  xMinutes: "{{count}}m",
  aboutXHours: "{{count}}h",
  xHours: "{{count}}h",
  xDays: "{{count}}d",
  aboutXWeeks: "{{count}}w",
  xWeeks: "{{count}}w",
  aboutXMonths: "{{count}}mo",
  xMonths: "{{count}}mo",
  aboutXYears: "{{count}}y",
  xYears: "{{count}}y",
};

function formatDistanceDisplay(token: string, count: number) {
  let tokenizedDate = "";
  if (count === 1) {
    tokenizedDate = formatSingleDistanceLocale[token];
  }
  if (!tokenizedDate) {
    tokenizedDate = formatDistanceLocale[token];
  }

  const result = tokenizedDate.replace("{{count}}", count.toString());

  return result;
}

export const relativeDate = (date: string) => {
  try {
    return formatDistance(parseISO(date), new Date(), {
      locale: {
        ...locale,
        formatDistance: formatDistanceDisplay,
      },
    });
  } catch {
    return "";
  }
};

export const timeUntil = (date: string) =>
  differenceInMinutes(parseISO(date), new Date());

export const getFormattedDate = (date: string) => {
  try {
    return format(parseISO(date), "E, h:mm a");
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

export const getFormattedFullDate = (date: string) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const dateUTC = parseISO(date);
    const zoned = utcToZonedTime(dateUTC, tz);
    const val = formatTz(zoned, "EEEE, LLLL do yyyy, 'at' h:mma z", {
      timeZone: tz,
    });
    return val;
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

// ie, "Tue, 11:00pm GMT"
export const getFormattedWeekdayWithTimeAndTimezone = (date: string) => {
  try {
    const tz = new Date()
      .toLocaleTimeString("en-us", { timeZoneName: "short" })
      .split(" ")[2];
    const parsedTime = parseISO(date);
    const formattedTime = format(parsedTime, "E, h:mma");

    return `${formattedTime.charAt(0) + formattedTime.slice(1).toLowerCase()} ${tz}`;
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

// ie, "Mon, Jan 1st"
export const getFormattedMonthWeekday = (date: string) => {
  try {
    const parsedTime = parseISO(date);
    const easternTime = utcToZonedTime(parsedTime, "America/New_York");
    /* This is setting the default to eastern when server rendered.
    If not the server renders the day based on UTC time which causes games
    after 8pm EST to be rendered as the next day. This sets a default that will 
    be the correct day for most US based users, while still updating client side 
    for the users local TZ */
    const formattedTime = format(
      isClient() ? parsedTime : easternTime,
      "EEE, MMM do"
    );

    return `${formattedTime}`;
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

// ie, "Oct 14 7:00pm ET"
export const getFormattedDateWithTimeAndTimezone = (date: Date) => {
  try {
    const tz = new Date()
      .toLocaleTimeString("en-us", { timeZoneName: "short" })
      .split(" ")[2];

    const formattedTime = format(date, "LLL d h:mma");

    return `${formattedTime.charAt(0) + formattedTime.slice(1).toLowerCase()} ${tz}`;
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

// ie, "Sunday"
export const getFormattedWeekday = (date: string) => {
  try {
    return format(parseISO(date), "EEEE");
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

// ie, "02/11"
export const getFormattedMonthDay = (date: string) => {
  try {
    return format(parseISO(date), "MM/dd");
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};
// ie, "Sat, Jan 13 2024"
export const getFormattedDayMonthYear = (date: string) => {
  try {
    return format(parseISO(date), "EEE, MMM d, yyyy");
  } catch (error) {
    logger.error("getFormattedDayMonthYear error", { date, error });
    return "";
  }
};

export const getFormattedTime = (date: string) => {
  try {
    return format(parseISO(date), "h:mm b");
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};
export const getFormattedTimeWithTZ = (date: string) => {
  try {
    const tz = new Date()
      .toLocaleTimeString("en-us", { timeZoneName: "short" })
      .split(" ")[2];
    return `${format(parseISO(date), "h:mm a").toLowerCase()} ${tz}`;
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

// ie "Sun, Feb 9, 2025 | 6:30PM EST"
export const getFormattedDayMonthYearTimeWithTZ = (date: string) => {
  const tz = getFormattedTimeWithTZ(date);
  const dayMonthYear = getFormattedDayMonthYear(date);
  if (tz && dayMonthYear) {
    return `${dayMonthYear} | ${tz}`;
  } else {
    return "";
  }
};

export const getFormattedDateTime = (date: string) => {
  try {
    const parsedDate = parseISO(date);
    // Use Intl API to format in Eastern Time for SEO metadata (server-side safe)
    // This prevents games after 8pm EDT from showing the next day's date in SSR
    // Server runs in UTC, so without this conversion, "2025-10-21T02:00:00Z" (10pm EDT Mon)
    // would display as "October 21" instead of the correct "October 20"
    const formatted = new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "America/New_York",
    }).format(parsedDate);
    return formatted;
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

export const getFormattedDateTimeNoYear = (date: string) => {
  try {
    return format(parseISO(date), "MMM d");
  } catch (error) {
    logger.error("getFormattedDate error", { date, error });
    return "";
  }
};

export const getDateRangeYesterday = (currentDate: Date) => {
  const start = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate() - 1
  );
  const end = add(new Date(start), { days: 1, minutes: -1 });

  return {
    start: `${formatISO(start)}`,
    end: `${formatISO(end)}`,
  };
};

export const getDateRangeToday = (currentDate: Date) => {
  const start = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate()
  );
  const end = add(new Date(start), { days: 1, minutes: -1 });

  return {
    start: `${formatISO(start)}`,
    end: `${formatISO(end)}`,
  };
};

export const getDateRangeThisWeek = (currentDate: Date) => {
  const start = previousSunday(currentDate);
  const end = add(new Date(currentDate), { hours: 12 });
  return {
    start: `${formatISO(start)}`,
    end: `${formatISO(end)}`,
  };
};

export const getDateRangeLastWeek = (currentDate: Date) => {
  const end = previousSunday(currentDate);
  const start = previousSunday(end);
  return {
    start: `${formatISO(start)}`,
    end: `${formatISO(end)}`,
  };
};

export const getDateRangeByDate = (
  year: number,
  month: number,
  day: number
) => {
  const start = new Date(year, month, day);
  const end = add(start, { days: 1, minutes: -1 });
  return {
    start: `${formatISO(start)}`,
    end: `${formatISO(end)}`,
  };
};

export const formatDateOption = (date: Date) => format(date, "MMMM d, yyyy");

export const MONTH_DATE = "M/d";
export const formatDate = (date: Date, formatStr: string) => {
  try {
    return format(date, formatStr);
  } catch (error) {
    logger.error(`formatDate error`, { date, formatStr, error });
    return "";
  }
};

export const add24Hours = (dateStr: string) => {
  try {
    const dateObj = new Date(dateStr);
    dateObj.setHours(dateObj.getHours() + 24);
    return dateObj.toISOString();
  } catch (error) {
    logger.error("add24Hours error", { dateStr, error });
    return "";
  }
};

export const isBeyondCurrentWeek = (isoDateString: string): boolean => {
  try {
    const now = new Date();
    const threshold = add(now, { days: 7 });
    const gameDate = parseISO(isoDateString);
    return gameDate > threshold;
  } catch (error) {
    logger.error("isBeyondCurrentWeek error", { isoDateString, error });
    return false;
  }
};

export const getFormattedMatchupHeaderDate = (
  isoDateString: string
): string => {
  try {
    if (isBeyondCurrentWeek(isoDateString)) {
      return getFormattedDayMonthYearTimeWithTZ(isoDateString);
    }
    return getFormattedWeekdayWithTimeAndTimezone(isoDateString);
  } catch (error) {
    logger.error("getFormattedMatchupHeaderDate error", {
      isoDateString,
      error,
    });
    return "";
  }
};
